import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Menu, LogOut, User, Bell, Smartphone } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { NotificationDropdown } from '@/components/notifications/notification-dropdown'
import { DemoAccountSwitcher } from '@/components/demo-account-switcher'
import { useAuthStore } from '@/store/auth-store'
import { useUIStore } from '@/store/ui-store'
import { cn } from '@/lib/utils'

export function Header() {
  const { user, logout } = useAuthStore()
  const { toggleSidebar, sidebarCollapsed } = useUIStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [qrModalOpen, setQrModalOpen] = useState(false)

  // Detect if we're inside an estimate or proposal detail page
  const isSettingsPreview = location.pathname.startsWith('/proposals/preview') && location.search.includes('mode=settings-preview')
  const isEstimateDetail = /^\/estimates\/[^/]+$/.test(location.pathname)
  const isProposalPreview = location.pathname === '/proposals/preview'
  const isProposalPreviewFromProposals = isProposalPreview && location.search.includes('from=/proposals')
  const isProposalDetail = !isSettingsPreview && !isProposalPreview && /^\/proposals\/[^/]+$/.test(location.pathname)
  const isRfpDetail = /^\/rfp-requests\/[^/]+$/.test(location.pathname)
  const isReportDetail = /^\/reports\/[^/]+$/.test(location.pathname)
  const isCustomerDetail = /^\/customers\/[^/]+$/.test(location.pathname)
  const isOpportunityDetail = /^\/opportunities\/[^/]+$/.test(location.pathname)
  const isInvoiceDetail = /^\/invoices\/[^/]+$/.test(location.pathname)
  const fromCustomer = (location.state as any)?.from === 'customer'
  const customerId = (location.state as any)?.customerId

  const initials = user?.displayName
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '??'

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 sm:px-6 transition-all duration-300',
        sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-68'
      )}
    >
      {/* Left: Mobile menu + Breadcrumb / Back link */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={toggleSidebar}
        >
          <Menu className="h-5 w-5" />
        </Button>
        <div className="hidden sm:block">
          {isEstimateDetail ? (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); navigate('/estimates') }}
              className="h-back"
            >
              <span>‹</span> Back to Estimates
            </a>
          ) : isRfpDetail ? (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); navigate('/rfp-requests') }}
              className="h-back"
            >
              <span>‹</span> Back to RFP Requests
            </a>
          ) : isProposalPreviewFromProposals ? (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); navigate('/proposals') }}
              className="h-back"
            >
              <span>‹</span> Back to Proposals
            </a>
          ) : isProposalDetail && fromCustomer && customerId ? (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); navigate(`/customers/${customerId}`) }}
              className="h-back"
            >
              <span>‹</span> Back to Customer
            </a>
          ) : isProposalDetail ? (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); navigate('/proposals') }}
              className="h-back"
            >
              <span>‹</span> Back to Proposals
            </a>
          ) : isReportDetail ? (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); navigate('/reports') }}
              className="h-back"
            >
              <span>‹</span> Back to Reports
            </a>
          ) : isCustomerDetail ? (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); navigate('/customers') }}
              className="h-back"
            >
              <span>‹</span> Back to Customers
            </a>
          ) : isOpportunityDetail ? (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); navigate('/opportunities') }}
              className="h-back"
            >
              <span>‹</span> Back to Opportunities
            </a>
          ) : isInvoiceDetail ? (
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); navigate('/invoices') }}
              className="h-back"
            >
              <span>‹</span> Back to Invoices
            </a>
          ) : (
            <p style={{ fontSize: '14px', color: 'var(--s500)' }}>
              Welcome back, <b style={{ color: 'var(--s900)', fontWeight: 600 }}>{user?.displayName}</b>
            </p>
          )}
        </div>
      </div>

      {/* Right: Notifications + User menu */}
      <div className="flex items-center gap-2">
        <DemoAccountSwitcher />
        <NotificationDropdown />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full">
              <Avatar className="h-9 w-9">
                {user?.avatarUrl && (
                  <AvatarImage src={user.avatarUrl} alt={user.displayName} />
                )}
                <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{user?.displayName}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings/profile')}>
              <User className="mr-2 h-4 w-4" />
              My Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings/notifications')}>
              <Bell className="mr-2 h-4 w-4" />
              User Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setQrModalOpen(true)}>
              <Smartphone className="mr-2 h-4 w-4" />
              Get the Mobile App
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* QR Code Modal — portaled to body so it's not constrained by <header> */}
      {qrModalOpen && createPortal(
        <div className="qr-modal-overlay" onClick={() => setQrModalOpen(false)}>
          <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
            <button className="qr-modal-close" onClick={() => setQrModalOpen(false)}>
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
            <div className="qr-modal-icon">
              <Smartphone className="h-6 w-6" />
            </div>
            <h3 className="qr-modal-title">Get the Paver Mobile App</h3>
            <p className="qr-modal-desc">Scan this QR code with your phone's camera to open Paver on your mobile device.</p>
            <div className="qr-modal-code">
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent('https://m.getpaver.io')}&bgcolor=ffffff&color=1e293b&margin=0`} alt="QR Code — m.getpaver.io" width="220" height="220" />
            </div>
            <p className="qr-modal-url">m.getpaver.io</p>
          </div>
        </div>,
        document.body
      )}
    </header>
  )
}
